import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import _get from 'lodash/get';
import { User } from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import PageOutOfRangeEmptyState from '@lib/components/PageOutOfRangeEmptyState/PageOutOfRangeEmptyState';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Search from '@lib/components/Search/Search';
import Table from '@lib/components/Table/Table';
import toast from '@lib/components/Toast/Toast';
import Trans from '@lib/components/Trans/Trans';
import useSearchParam from '@lib/hooks/useSearchParam';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import { Modals } from '@lib/enums/modals';
import getUsersTableActionsForSelected from 'routes/Users/utils/getUsersTableActionsForSelected';
import useUsersActions from 'routes/Users/utils/useUsersActions';
import UserModalForm from 'components/UserModalForm/UserModalForm';
import { UseTeamQueriesResult } from 'routes/Teams/utils/useTeamQueries';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { IconName } from '@lib/components/Modal/enums';
import getTeamMembersTableColumns from '../utils/getTeamMembersTableColumns';
import getTeamMembersTableActionsMenu from '../utils/getTeamMembersTableActionsMenu';
import useMutations from '../utils/useMutations';
import { UseTeamActionsResult } from '../utils/useTeamActions';
import { TeamViewMode } from '../enums';

type Props = {
  useModalManagementResult: UseModalManagementResult;
  useTeamActionsResult: UseTeamActionsResult;
  useTeamQueriesResult: UseTeamQueriesResult;
  isViewOnly: boolean;
};

export default function TeamViewMembers({
  useModalManagementResult,
  useTeamActionsResult,
  useTeamQueriesResult,
  isViewOnly,
}: Props) {
  const { t } = useTranslation();
  const { id: teamId = '' } = useParams();
  const [searchQuery] = useSearchParam();
  const useBulkSelectResult = useTableItemsBulkSelect<User>();
  const { useUsersQueryHookResult, useTeamQueryHookResult } =
    useTeamQueriesResult;
  const { onViewChange } = useTeamActionsResult;
  const { data } = useTeamQueryHookResult;
  const { name, color, readOnly } = data || {};
  const isUpdateAvailable = !readOnly;

  const { isModalOpened, closeModal, openModal, getModalData } =
    useModalManagementResult;

  const {
    currentUserId,
    onActivate,
    onBulkActivate,
    onBulkDeactivate,
    onConfirmedTeamMembersUpdate,
    onDeactivate,
    onEdit,
    onEditAccesses,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    onSubmitUserFormCallback,
    onTeamMembersUpdate,
    openUserViewPage,
    selectedUserId,
    teamsLoadOptions,
  } = useUsersActions({
    useUsersQueryHookResult,
    useBulkSelectResult,
    useModalManagementResult,
  });

  const { collection, loading, firstLoading, error, totalCount, refetch } =
    useUsersQueryHookResult;

  const { teamUpdateMutation, teamUpdateMutationResult } = useMutations();

  const onRemoveMember = (item?: User) => {
    if (item?.id) {
      openModal(Modals.TeamMemberRemoveModal, { userId: item.id });
    }
  };

  const onConfirmedRemoveMember = () => {
    const userId = _get(
      getModalData(Modals.TeamMemberRemoveModal),
      'userId',
      '',
    ) as string;
    if (!userId || !teamId) return;
    teamUpdateMutation({
      variables: {
        id: teamId,
        attributes: { users: [{ id: userId, unlink: true }] },
      },
      onCompleted: () => {
        closeModal(Modals.TeamMemberRemoveModal);
        toast({ content: t('remove-member-success') });
        refetch();
      },
    });
  };

  const tableColumns = getTeamMembersTableColumns({
    t,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    isViewOnly,
    teamColor: color,
    teamName: name,
  });
  const tableActionsMenu = getTeamMembersTableActionsMenu({
    currentUserId,
    isViewOnly,
    onActivate,
    onDeactivate,
    onEdit,
    onEditAccesses,
    onRemoveMember,
    onView: openUserViewPage,
    t,
  });
  const tableActionsForSelected = getUsersTableActionsForSelected({
    onBulkActivate,
    onBulkDeactivate,
    onTeamMembersUpdate,
    selectedItemsCount: useBulkSelectResult.getSelectedItemsCount(totalCount),
    t,
    teamsLoadOptions,
    isViewOnly,
  });

  const isEmptyStateVisible = collection.length === 0 && !error && !loading;
  const isEmptyStateByOutOfRangePage = isEmptyStateVisible && !!totalCount;
  let emptyState: React.ReactNode = null;
  if (isEmptyStateByOutOfRangePage) {
    emptyState = <PageOutOfRangeEmptyState totalEntries={totalCount} />;
  } else if (isEmptyStateVisible) {
    emptyState = (
      <ListEmptyState
        title={t('no-members-yet')}
        text={t('no-members-text')}
        actions={
          isUpdateAvailable
            ? [
                <Button
                  key="edit"
                  buttonText={t('edit-team')}
                  buttonType={ButtonTypes.primaryFilled}
                  leftIcon="edit"
                  onClick={() => onViewChange(TeamViewMode.Edit)}
                  disabled={loading}
                />,
              ]
            : []
        }
      />
    );
  }

  const isErrorStateVisible = !!error && !loading;
  const errorState = isErrorStateVisible ? (
    <ErrorPage
      status={getStatusFromError(error)}
      description={getDescriptionFromError(error)}
    />
  ) : null;

  const isContentVisible = !error && !firstLoading && collection?.length;
  const content = isContentVisible ? (
    <>
      {(!!collection?.length || searchQuery) && (
        <Search placeholder={t('member-search-placeholder')} />
      )}
      <Table
        actionsForSelected={tableActionsForSelected}
        actionsMenu={tableActionsMenu}
        columns={tableColumns}
        data={collection}
        emptyState={emptyState}
        // fullHeight
        id="users"
        loading={loading}
        totalEntries={totalCount}
        useBulkSelectResult={useBulkSelectResult}
      />
    </>
  ) : null;

  return (
    <>
      <WhiteBox>
        <DashboardTitleHeader
          title={name}
          actions={[
            isUpdateAvailable && !isViewOnly ? (
              <Button
                key="edit"
                buttonText={t('edit')}
                buttonType={ButtonTypes.primaryOutlined}
                leftIcon="edit"
                onClick={() => onViewChange(TeamViewMode.Edit)}
                disabled={loading}
              />
            ) : undefined,
            !isUpdateAvailable || isViewOnly ? (
              <ViewOnlyChip key="view" />
            ) : undefined,
          ]}
        />
        {firstLoading && <CircleLoader />}
        {errorState}
        {emptyState}
        {content}
      </WhiteBox>
      <UserModalForm
        isOpen={isModalOpened(Modals.UserFormModal)}
        onClose={() => closeModal(Modals.UserFormModal)}
        onSubmitUserFormCallback={onSubmitUserFormCallback}
        selectedUserId={selectedUserId}
        title={selectedUserId ? t('edit-team-member') : t('add-team-member')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-move-users')}
        description={t('move-users--to-team-text')}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.MoveMembersToTeamModal)}
        onClose={() => closeModal(Modals.MoveMembersToTeamModal)}
        onConfirm={onConfirmedTeamMembersUpdate}
        title={t('are-you-sure')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-remove-user')}
        description={<Trans i18nKey="remove-member-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.TeamMemberRemoveModal)}
        loading={teamUpdateMutationResult.loading}
        onClose={() => closeModal(Modals.TeamMemberRemoveModal)}
        onConfirm={onConfirmedRemoveMember}
        title={t('remove-member')}
      />
    </>
  );
}
